// MWA-385 — merge rules for the live client Tasks tab. Kept free of React so the rules
// can be tested without a DOM.

import type { Task } from '../../services/api'

export type TaskEventAction = 'task_created' | 'task_updated' | 'task_deleted'

function shallowEqual(a: Task, b: Task): boolean {
  const aKeys = Object.keys(a) as (keyof Task)[]
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => a[key] === b[key])
}

function removeById(list: Task[], id: string): Task[] {
  return list.some((t) => t.id === id) ? list.filter((t) => t.id !== id) : list
}

/**
 * Apply one live task message to the list the portal shows. Returns the same array when
 * the message changes nothing, so a repeated message does not re-render.
 */
export function applyTaskEvent(list: Task[], action: string, data: unknown): Task[] {
  if (!data || typeof data !== 'object') return list
  const payload = data as Partial<Task> & { id?: string }
  if (typeof payload.id !== 'string' || !payload.id) return list

  if (action === 'task_deleted') return removeById(list, payload.id)
  if (action !== 'task_created' && action !== 'task_updated') return list

  // A task switched to not client-facing, or soft-deleted, leaves the client's list
  // exactly as a delete does.
  if (payload.isClientFacing === false || payload.isDeleted === true) {
    return removeById(list, payload.id)
  }

  const incoming = payload as Task
  const index = list.findIndex((t) => t.id === incoming.id)
  if (index === -1) return [...list, incoming]

  const existing = list[index]
  // Messages can arrive out of order; an older copy must not overwrite a newer one.
  if (existing.updatedAt && incoming.updatedAt && incoming.updatedAt < existing.updatedAt) {
    return list
  }
  if (shallowEqual(existing, incoming)) return list
  const next = list.slice()
  next[index] = incoming
  return next
}

/** Remove the given ids from a list, returning the same array when none are present. */
export function withoutIds(list: Task[], ids: ReadonlySet<string>): Task[] {
  if (ids.size === 0 || !list.some((t) => ids.has(t.id))) return list
  return list.filter((t) => !ids.has(t.id))
}
